import type { HTMLAttributes, ReactNode } from 'react';
import { Text as SCText } from '@sitecore-jss/sitecore-jss-nextjs';

import type { Field } from 'models/sitecore';
import type { HTMLTagName } from 'types';
import { cn } from 'utils/classNames';

export type Tags =
	| 'caption'
	| 'cursiveHeader'
	| 'div'
	| 'h1'
	| 'h2'
	| 'h3'
	| 'h4'
	| 'h5'
	| 'h6'
	| 'highlightLg'
	| 'highlightMd'
	| 'highlightSm'
	| 'highlightXl'
	| 'highlightXs'
	| 'p'
	| 'pLarge'
	| 'pSmall'
	| 'pXSmall'
	| 'screenReader'
	| 'span'
	| 'strong';

interface Props extends HTMLAttributes<HTMLElement> {
	/** The html-tag/Component that will be outputted */
	as: Tags;

	/** The text to render (takes precedence over the text prop) */
	children?: ReactNode;

	/** Pass optional classnames for the html */
	className?: string;

	/** Sitecore field for handling Sitecore Experience Editor */
	field?: Partial<Field<string | number>>;

	/** Sometimes we need the tag to be present, although empty, for aria id to not reference nothing */
	renderEmpty?: boolean;

	/** The style that will be used (instead of the tag default style) */
	styleAs?: Tags;

	/** The text to render (if no children are present) */
	text?: string;
}

const tagMapper: Record<Tags, HTMLTagName> = {
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	h5: 'h5',
	h6: 'h6',
	highlightXs: 'p',
	highlightSm: 'p',
	highlightMd: 'p',
	highlightLg: 'p',
	highlightXl: 'p',
	p: 'p',
	pLarge: 'p',
	pSmall: 'p',
	pXSmall: 'p',
	div: 'div',
	span: 'span',
	strong: 'strong',
	caption: 'caption',
	screenReader: 'span',
	cursiveHeader: 'h2',
} as const;

export const FONT_CLASSES: Record<Tags, string> = {
	// Match heading sizes with richtext.js
	h1: 'font-heading font-bold text-h1',
	h2: 'font-heading font-bold text-h2',
	h3: 'font-heading font-bold text-h3',
	h4: 'font-heading font-bold text-h4',
	h5: 'font-heading font-bold text-h5',
	h6: 'font-heading font-bold text-h6',
	highlightXs: 'font-heading text-highlight-xs',
	highlightSm: 'font-heading text-highlight-sm',
	highlightMd: 'font-heading text-highlight-md',
	highlightLg: 'font-heading text-highlight-lg',
	highlightXl: 'font-heading text-highlight-xl',
	p: 'font-body text-md',
	pXSmall: 'font-body text-xs',
	pSmall: 'font-body text-sm',
	pLarge: 'font-body text-lg',
	div: 'font-body text-md',
	span: 'font-body text-md',
	strong: 'font-body text-md font-bold',
	caption: 'font-body text-md font-bold',
	screenReader: 'sr-only',
	cursiveHeader: 'font-caveat text-[2.25rem]',
} as const;

/** Text component to render texts in all different variations */
export default function Text({
	as,
	children,
	className = '',
	field,
	renderEmpty = false,
	styleAs,
	text,
	...htmlAttributes
}: Props) {
	const hasFieldWithValue = Boolean(
		field?.value !== undefined && field.value !== '',
	);
	// Field.editable means it's currently the experience editor, where fields
	// can be displayed as placeholders when empty.
	const hasContent = Boolean(
		children || text || hasFieldWithValue || field?.editable,
	);
	if (!hasContent && !renderEmpty) {
		return null;
	}
	const content = children || text;

	const Component = tagMapper[as];
	const appearanceTag = styleAs ?? as;
	const isScreenReader = appearanceTag === 'screenReader';

	return (
		<Component
			className={cn(
				FONT_CLASSES[appearanceTag],
				className,
				!isScreenReader && 'wrap-break-word',
			)}
			{...htmlAttributes}
		>
			{field && <SCText field={field} />}
			{!field && <>{content} </>}
		</Component>
	);
}
Text.displayName = 'Text';
